<template>
  <div>
    <LoadingIndicatorComponent v-if="isLoading" />

    <div v-else>
      <!-- Create Invoice Form -->
      <div class="card p-3 mb-4" style="max-width: 600px">
        <h5 class="mb-3">Create Flat-Fee Invoice</h5>
        <form @submit.prevent="createInvoice">
          <div class="row mb-3">
            <div class="col-md-6">
              <label class="form-label">Period Start</label>
              <input
                v-model="form.period_start"
                type="date"
                class="form-control"
                required
              />
            </div>
            <div class="col-md-6">
              <label class="form-label">Period End</label>
              <input
                v-model="form.period_end"
                type="date"
                class="form-control"
                required
              />
            </div>
          </div>
          <div class="mb-3">
            <label class="form-label">Amount (EUR)</label>
            <input
              v-model.number="form.amount"
              type="number"
              step="0.01"
              min="0.01"
              class="form-control"
              required
            />
          </div>
          <div class="mb-3">
            <label class="form-label">Description</label>
            <input
              v-model="form.description"
              type="text"
              class="form-control"
              required
            />
          </div>
          <button type="submit" class="btn btn-primary" :disabled="isCreating">
            <font-awesome-icon
              v-if="isCreating"
              icon="spinner"
              spin
              class="me-1"
            />
            Create Invoice
          </button>
        </form>
      </div>

      <!-- Invoices List -->
      <h5 class="mb-2">Flat-Fee Invoices</h5>
      <div v-if="invoices.length === 0" class="text-muted mb-4">
        No flat-fee invoices yet.
      </div>
      <table v-else class="table table-sm mb-4">
        <thead>
          <tr>
            <th style="width: 30px"></th>
            <th>Invoice #</th>
            <th>Period</th>
            <th>Amount</th>
            <th>Status</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody v-for="inv in invoices" :key="inv.id">
          <tr>
            <td>
              <button
                class="btn btn-sm btn-link p-0"
                @click="toggleCosts(inv.id)"
              >
                <font-awesome-icon
                  :icon="
                    expandedInvoiceId === inv.id
                      ? 'chevron-down'
                      : 'chevron-right'
                  "
                />
              </button>
            </td>
            <td>{{ inv.invoice_number }}</td>
            <td>{{ inv.period_start }} — {{ inv.period_end }}</td>
            <td>{{ inv.display_amount?.toFixed(2) ?? "—" }} EUR</td>
            <td>
              <span :class="statusBadgeClass(inv)">{{ statusLabel(inv) }}</span>
            </td>
            <td>{{ formatDate(inv.created_at) }}</td>
            <td>
              <button
                v-if="!inv.paid_at && !inv.canceled_at"
                class="btn btn-sm btn-outline-success me-1"
                :disabled="actionInProgress === inv.id"
                @click="markPaid(inv.id)"
              >
                Mark Paid
              </button>
              <button
                v-if="!inv.canceled_at"
                class="btn btn-sm btn-outline-danger"
                :disabled="actionInProgress === inv.id"
                @click="cancelInvoice(inv.id)"
              >
                Cancel
              </button>
            </td>
          </tr>
          <!-- Expanded costs row -->
          <tr v-if="expandedInvoiceId === inv.id">
            <td colspan="7" class="bg-light p-3">
              <LoadingIndicatorComponent
                v-if="costsLoading"
                inline
                message="Loading costs…"
              />
              <div
                v-else-if="expandedCosts.length === 0"
                class="text-muted small"
              >
                No associated registry operation costs.
              </div>
              <div v-else>
                <strong class="small"
                  >Associated COGS ({{ expandedCosts.length }})</strong
                >
                <table class="table table-sm table-borderless mt-1 mb-0">
                  <thead>
                    <tr class="small text-muted">
                      <th>Type</th>
                      <th>Region</th>
                      <th>MWh</th>
                      <th>Unit Price</th>
                      <th>Total</th>
                      <th>Date</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="cost in expandedCosts" :key="cost.id">
                      <td>{{ formatCostType(cost.cost_type) }}</td>
                      <td>{{ cost.region }}</td>
                      <td>{{ cost.amount }}</td>
                      <td>{{ cost.unit_price }}</td>
                      <td>
                        {{
                          (cost.amount * parseFloat(cost.unit_price)).toFixed(2)
                        }}
                        EUR
                      </td>
                      <td>{{ formatDate(cost.incurred_at) }}</td>
                      <td>
                        <button
                          v-if="cost.can_unassociate"
                          class="btn btn-sm btn-link text-danger p-0"
                          title="Unlink from invoice (outside period)"
                          @click="unassociateCost(cost.id)"
                        >
                          Unlink
                        </button>
                      </td>
                    </tr>
                    <tr class="fw-bold small">
                      <td colspan="4">Total COGS</td>
                      <td>{{ totalCogs.toFixed(2) }} EUR</td>
                      <td colspan="2"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Unassociated Costs -->
      <h5 class="mb-2">Unassociated Registry Operation Costs</h5>
      <div v-if="unassociatedCosts.length === 0" class="text-muted">
        No unassociated costs.
      </div>
      <table v-else class="table table-sm">
        <thead>
          <tr>
            <th>Type</th>
            <th>Region</th>
            <th>Amount (MWh)</th>
            <th>Unit Price</th>
            <th>Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="cost in unassociatedCosts" :key="cost.id">
            <td>{{ formatCostType(cost.cost_type) }}</td>
            <td>{{ cost.region }}</td>
            <td>{{ cost.amount }}</td>
            <td>{{ cost.unit_price }} EUR/MWh</td>
            <td>{{ formatDate(cost.incurred_at) }}</td>
            <td>
              <select
                class="form-select form-select-sm d-inline-block"
                style="width: auto"
                @change="
                  associateCost(
                    cost.id,
                    ($event.target as HTMLSelectElement).value
                  )
                "
              >
                <option value="">Link to invoice…</option>
                <option
                  v-for="inv in activeInvoices"
                  :key="inv.id"
                  :value="inv.id"
                >
                  {{ inv.invoice_number }} ({{ inv.period_start }} —
                  {{ inv.period_end }})
                </option>
              </select>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type { Invoice } from "@/types";
import { computed, inject, ref, watch } from "vue";

import type { RegistryOperationCost } from "./types";

const props = defineProps<{
  companyId: number;
}>();

const isLoading = ref(false);
const isCreating = ref(false);
const actionInProgress = ref<number | null>(null);
const invoices = ref<Invoice[]>([]);
const unassociatedCosts = ref<RegistryOperationCost[]>([]);
const expandedInvoiceId = ref<number | null>(null);
const expandedCosts = ref<RegistryOperationCost[]>([]);
const costsLoading = ref(false);

const form = ref({
  period_start: "",
  period_end: "",
  amount: null as number | null,
  description: "",
});

const activeInvoices = computed(() =>
  invoices.value.filter((inv) => !inv.canceled_at)
);

const totalCogs = computed(() =>
  expandedCosts.value.reduce(
    (sum, c) => sum + c.amount * parseFloat(c.unit_price),
    0
  )
);

let loadRequestId = 0;

async function loadData() {
  expandedInvoiceId.value = null;
  expandedCosts.value = [];
  isLoading.value = true;
  const currentRequestId = ++loadRequestId;
  try {
    const [invRes, costsRes] = await Promise.all([
      goSolidApi.get(`/api/admin/company/${props.companyId}/flat-fee-invoices`),
      goSolidApi.get(
        `/api/admin/company/${props.companyId}/registry-operation-costs?unassociated_only=true`
      ),
    ]);
    if (currentRequestId !== loadRequestId) return;
    invoices.value = invRes.data.invoices ?? [];
    unassociatedCosts.value = costsRes.data.costs ?? [];
  } catch (error: unknown) {
    if (currentRequestId !== loadRequestId) return;
    globalErrorHandler(error);
  } finally {
    if (currentRequestId === loadRequestId) {
      isLoading.value = false;
    }
  }
}

async function toggleCosts(invoiceId: number) {
  if (expandedInvoiceId.value === invoiceId) {
    expandedInvoiceId.value = null;
    expandedCosts.value = [];
    return;
  }
  expandedInvoiceId.value = invoiceId;
  await refreshExpandedCosts();
}

async function refreshExpandedCosts() {
  if (!expandedInvoiceId.value) return;
  costsLoading.value = true;
  try {
    const res = await goSolidApi.get(
      `/api/admin/company/${props.companyId}/flat-fee-invoices/${expandedInvoiceId.value}/costs`
    );
    expandedCosts.value = res.data.costs ?? [];
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    costsLoading.value = false;
  }
}

async function createInvoice() {
  isCreating.value = true;
  try {
    await goSolidApi.post(
      `/api/admin/company/${props.companyId}/flat-fee-invoices`,
      {
        amount: form.value.amount,
        description: form.value.description,
        period_start: form.value.period_start,
        period_end: form.value.period_end,
      }
    );
    form.value = {
      period_start: "",
      period_end: "",
      amount: null,
      description: "",
    };
    await loadData();
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    isCreating.value = false;
  }
}

async function markPaid(invoiceId: number) {
  if (actionInProgress.value) return;
  actionInProgress.value = invoiceId;
  try {
    await goSolidApi.put(
      `/api/admin/company/${props.companyId}/flat-fee-invoices/${invoiceId}/paid`
    );
    await loadData();
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    actionInProgress.value = null;
  }
}

async function cancelInvoice(invoiceId: number) {
  if (actionInProgress.value) return;
  actionInProgress.value = invoiceId;
  try {
    await goSolidApi.delete(
      `/api/admin/company/${props.companyId}/flat-fee-invoices/${invoiceId}`
    );
    await loadData();
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    actionInProgress.value = null;
  }
}

async function associateCost(costId: number, invoiceIdStr: string) {
  if (!invoiceIdStr) return;
  try {
    await goSolidApi.post(
      `/api/admin/company/${props.companyId}/registry-operation-costs/${costId}/associate`,
      { invoice_id: parseInt(invoiceIdStr) }
    );
    await loadData();
    await refreshExpandedCosts();
  } catch (error: unknown) {
    globalErrorHandler(error);
  }
}

async function unassociateCost(costId: number) {
  try {
    await goSolidApi.delete(
      `/api/admin/company/${props.companyId}/registry-operation-costs/${costId}/associate`
    );
    await loadData();
    await refreshExpandedCosts();
  } catch (error: unknown) {
    globalErrorHandler(error);
  }
}

function statusLabel(inv: Invoice): string {
  if (inv.canceled_at) return "Canceled";
  if (inv.paid_at) return "Paid";
  if (inv.requested_at) return "Sent";
  return "Draft";
}

function statusBadgeClass(inv: Invoice): string {
  if (inv.canceled_at) return "badge bg-danger";
  if (inv.paid_at) return "badge bg-success";
  if (inv.requested_at) return "badge bg-primary";
  return "badge bg-secondary";
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString();
}

function formatCostType(type: string): string {
  return type
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const globalErrorHandler = inject("globalErrorHandler") as (error: any) => void;

watch(() => props.companyId, loadData, { immediate: true });
</script>
